import { useMemo } from 'react';
import styled from 'styled-components';
import { LoadingContainer } from '@frameio/vapor-migration';
import type {
  Project,
  Workspace,
} from '@frameio/workspaces/shared/components/WorkspaceProjectList';
import { ProjectHeaderBackButton } from '../../components/ProjectHeader';
import { useCachedEntities, useProjectFolders } from '../../hooks';
import { ProjectTree } from '../../ProjectTree';
import type { ProjectTreeItem, WindowedProjectTree } from '../../types';
import TEXT from './generated/TEXT/FoldersView';

type FoldersViewProps = {
  onBackSelect: () => void;
  onFolderSelect: (folderId: string) => void;
  project?: Project;
  targetFolderId?: string;
  workspace?: Workspace;
};

export function FoldersView({
  onBackSelect,
  onFolderSelect,
  project: projectProp,
  targetFolderId,
  workspace: workspaceProp,
}: FoldersViewProps) {
  const { project, workspace } = useCachedEntities<
    Pick<FoldersViewProps, 'project' | 'workspace'>
  >({
    project: projectProp,
    workspace: workspaceProp,
  });

  const { error, fetchMoreFolders, isLoading, toggleFolder, tree } =
    useProjectFolders(project?.rootAssetId, true);

  const wrappedTree = useMemo<WindowedProjectTree>(
    () => ({
      children: [tree],
      isOpen: true,
      props: { id: 'hidden-root', type: 'hidden-root' },
    }),
    [tree]
  );

  const selectedItem = useMemo<ProjectTreeItem>(
    () => ({
      id: targetFolderId ?? '',
      type: project?.rootAssetId === targetFolderId ? 'root-folder' : 'folder',
    }),
    [project?.rootAssetId, targetFolderId]
  );

  if (!project) return null;

  const header = (
    <ProjectHeaderBackButton
      avatarUrl={project.images?.image32 ?? ''}
      details={workspace?.name ?? ''}
      isPrivate={project.private}
      onSelect={onBackSelect}
      projectId={project.id}
      projectName={project.name}
    />
  );

  const handleItemSelect = (item: ProjectTreeItem) => {
    onFolderSelect(item.id);
  };

  return (
    <Grid>
      {header}
      <LoadingContainer
        key={project?.id}
        errorMessage={error ? TEXT.errorGettingProject : undefined} // force remount when project changes
        isLoading={isLoading}
        spinnerDelay={0}
        style={{ overflow: 'hidden' }}
      >
        {!isLoading && (
          <ProjectTree
            disableContextMenus={true}
            disableOpenOnDesktop={true}
            dndNamespace="move-to-copy-to"
            onBoundarySeen={fetchMoreFolders}
            onItemSelect={handleItemSelect}
            paddingX={FOLDER_TREE_PADDING_X}
            paddingY={FOLDER_TREE_PADDING_Y}
            rootFolderId={project.rootAssetId}
            selectedItem={selectedItem}
            toggleItem={toggleFolder}
            tree={wrappedTree}
          />
        )}
      </LoadingContainer>
    </Grid>
  );
}

const FOLDER_TREE_PADDING_X = 16;
const FOLDER_TREE_PADDING_Y = 8;

TEXT.DEFINE({
  errorGettingProject: 'An error has occurred retrieving the Project',
});

const Grid = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr;
  height: 100%;
  overflow: hidden;
`;
